import { motion } from 'framer-motion';
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../api/config';
import { useAuth } from '../contexts/AuthContext';

interface Flow {
  id: string;
  flowName: string;
  llmProvider?: 'openai' | 'gemini';
  minQuestions: number;
  maxQuestions: number;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const AdminDashboard: React.FC = () => {
  const [flows, setFlows] = useState<Flow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchFlows();
  }, []);

  const fetchFlows = async () => {
    try {
      const response = await api.get('/flows');
      setFlows(response.data);
    } catch (err) {
      setError('Failed to fetch flows');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string, flowName: string) => {
    if (
      window.confirm(`Are you sure you want to delete the flow "${flowName}"?`)
    ) {
      try {
        await api.delete(`/flows/${id}`);
        setFlows(flows.filter((flow) => flow.id !== id));
      } catch (err) {
        alert('Failed to delete flow');
      }
    }
  };

  const handleDownload = async (id: string) => {
    try {
      const response = await api.get(`/flows/${id}/csv`, {
        responseType: 'blob', // important!
        headers: {
          Accept: 'text/csv',
        },
      });

      // Create a blob URL
      const url = window.URL.createObjectURL(new Blob([response.data]));

      // Create a link and trigger download
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'report_' + id + '.csv');
      document.body.appendChild(link);
      link.click();

      // Cleanup
      if (link && link.parentNode) link.parentNode.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
    } finally {
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <h1 className="text-3xl font-bold text-gray-900">Flow Dashboard</h1>
            <div className="flex items-center space-x-4">
              <span className="text-sm text-gray-600">
                Welcome, {user?.username}
              </span>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-500 hover:text-gray-700"
              >
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
        <div className="px-4 py-6 sm:px-0">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold text-gray-900">Your Flows</h2>
            <Link
              to="/admin/flows/new"
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
              Create New Flow
            </Link>
          </div>

          {error && (
            <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {flows.length === 0 ? (
            <div className="text-center py-12">
              <svg
                className="mx-auto h-12 w-12 text-gray-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
              <h3 className="mt-2 text-sm font-medium text-gray-900">
                No flows
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Get started by creating a new flow.
              </p>
              <div className="mt-6">
                <Link
                  to="/admin/flows/new"
                  className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  Create New Flow
                </Link>
              </div>
            </div>
          ) : (
            <div className="bg-white shadow overflow-hidden sm:rounded-md">
              <ul className="divide-y divide-gray-200">
                {flows.map((flow, index) => (
                  <motion.li
                    key={flow.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="px-4 py-4 sm:px-6">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <div className="flex-shrink-0">
                            <div
                              className={`h-10 w-10 rounded-full flex items-center justify-center ${
                                flow.isActive ? 'bg-green-100' : 'bg-gray-100'
                              }`}
                            >
                              <svg
                                className={`h-6 w-6 ${
                                  flow.isActive
                                    ? 'text-green-600'
                                    : 'text-gray-400'
                                }`}
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                                />
                              </svg>
                            </div>
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-gray-900">
                              {flow.flowName}
                            </div>
                            <div className="text-sm text-gray-500">
                              {flow.minQuestions}-{flow.maxQuestions} AI questions
                              {' • '}
                              {flow.llmProvider === 'gemini'
                                ? 'Gemini'
                                : 'OpenAI'}
                              {' • Created '}
                              {new Date(flow.createdAt).toLocaleDateString()}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <Link
                            to={`/admin/flows/${flow.id}/preview`}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Preview
                          </Link>
                          <Link
                            to={`/admin/flows/${flow.id}/edit`}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            Edit
                          </Link>
                          <button
                            onClick={() => handleDelete(flow.id, flow.flowName)}
                            className="text-red-600 hover:text-red-900 text-sm font-medium"
                          >
                            Delete
                          </button>
                          <button
                            onClick={() => handleDownload(flow.id)}
                            className="text-blue-600 hover:text-blue-900 text-sm font-medium"
                          >
                            CSV
                          </button>
                          <a
                            href={`/${flow.flowName}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-green-600 hover:text-green-900 text-sm font-medium"
                          >
                            View Live
                          </a>
                        </div>
                      </div>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminDashboard;
